'use client';

import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';
import { UserGetInfoResponse } from '@trucksmarter/shared';
import BrokerProductSupportProvider from '~/app/_ui/shared/BrokerProductSupportProvider/BrokerProductSupportProvider';
import BrokersProvider from '~/app/_ui/shared/BrokersProvider/BrokersProvider';
import UserProvider from '../UserProvider/UserProvider';
import fetchUserInfo from '@lib/api/client/fetch-user-info';
import ImpressionsProvider from '../ImpressionsProvider/ImpressionsProvider';
import FinancialProductsConfigProvider from '../FinancialProductsProvider/FinancialProductsProvider';

const RefreshUserInfoContext = createContext<(() => void) | null>(null);

export function useRefreshUserInfo() {
  const refreshUserInfo = useContext(RefreshUserInfoContext);

  if (refreshUserInfo === null) {
    throw new Error(
      'useRefreshUserInfo has to be used within <UserInfoManager>',
    );
  }

  return refreshUserInfo;
}

interface Props {
  initialUserInfoData: UserGetInfoResponse;
}

export default function UserInfoManager({
  initialUserInfoData,
  children,
}: PropsWithChildren<Props>) {
  const {
    userInfo: {
      user,
      brokers,
      productSupportMap,
      impressions,
      financialProducts,
    },
    refresh: refreshUserInfo,
  } = useUserInfo(initialUserInfoData);

  return (
    <RefreshUserInfoContext.Provider value={refreshUserInfo}>
      <UserProvider value={user}>
        <ImpressionsProvider data={impressions}>
          <BrokerProductSupportProvider value={productSupportMap}>
            <FinancialProductsConfigProvider value={financialProducts}>
              <BrokersProvider value={brokers}>{children}</BrokersProvider>
            </FinancialProductsConfigProvider>
          </BrokerProductSupportProvider>
        </ImpressionsProvider>
      </UserProvider>
    </RefreshUserInfoContext.Provider>
  );
}

function useUserInfo(initialValue: UserGetInfoResponse) {
  const [userInfo, setUserInfo] = useState(initialValue);
  const refresh = useCallback(() => fetchUserInfo().then(setUserInfo), []);

  return { userInfo, refresh };
}
